import React, { useMemo } from 'react'
import styled from 'styled-components'
import { FaCog, FaRedoAlt } from 'react-icons/fa'
import { PredictionStatisticsBody } from '../../../../types/prediction'
import { Tag } from '../../../../types/models/tag'
import { getContrastYIQ } from '../../../../utils'
import { useGetPredictionStatisticsQuery } from '../../../../services/predictionApi'
import { useGetModelQuery } from '../../../../services/modelsApi'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedModelId } from '../../../../store/slices/modelSlice'
import { Button } from 'react-bootstrap'
import { Sliders } from 'lucide-react'

const DistributionHeader = styled.div`
  color: white;
  font-weight: 600;
  margin-bottom: 1rem;
`

const StatsContainer = styled.div`
  padding: 1rem;
`

const StatsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h5 {
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
  }

  button {
    padding: 0;
    color: #6c757d;
    &:hover {
      color: #fff;
    }
  }
`

const StatsContent = styled.div`
  margin-bottom: 1rem;
`

const DistributionBar = styled.div`
  display: flex;
  height: 24px;
  background: #2c2c2c;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
`

const BarSegment = styled.div<{
  $width: number
  $color: string
  $isActive: boolean
}>`
  width: ${(props) => props.$width}%;
  background-color: ${(props) => props.$color};
  opacity: ${(props) => (props.$isActive ? 1 : 0.6)};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const DistributionLegend = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.5rem;
`

const LegendItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  background: ${(props) => (props.$isActive ? '#3c3c3c' : 'transparent')};

  &:hover {
    background: #3c3c3c;
  }

  span {
    font-size: 0.9rem;
    color: ${(props) => (props.$isActive ? '#fff' : '#a0a0a0')};
  }
`

const LegendColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${(props) => props.$color};
`

interface InferenceStatsProps {
  confidenceDistribution: {
    none: number
    high: number
    medium: number
    low: number
  } | null
  confidenceRanges: { high: number; medium: number }
  activeTab: 'none' | 'high' | 'medium' | 'low'
  tags: Tag[] | undefined
  onTabChange: (tab: 'none' | 'high' | 'medium' | 'low') => void
  onOpenSettings: () => void
  bboxThreshold: number
  shouldShowEmptyState: boolean
}

const InferenceStats: React.FC<InferenceStatsProps> = ({
  confidenceDistribution,
  confidenceRanges,
  activeTab,
  tags,
  onTabChange,
  onOpenSettings,
  bboxThreshold,
  shouldShowEmptyState,
}) => {
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const { data: predictionStats, isLoading } = useGetPredictionStatisticsQuery(
    {
      model_id: selectedModel?.id || '',
      uncertainty_threshold: bboxThreshold,
    },
    {
      skip: !selectedModel?.id,
    }
  )

  const total = useMemo(() => {
    if (!confidenceDistribution) return 0
    return (
      confidenceDistribution.none +
      confidenceDistribution.high +
      confidenceDistribution.medium +
      confidenceDistribution.low
    )
  }, [confidenceDistribution])

  const getPercentage = (count: number) => {
    if (!total || !count) return 0
    return (count / total) * 100
  }

  if (shouldShowEmptyState) {
    return (
      <StatsContainer>
        <StatsHeader>
          <h5>Confidence Distribution</h5>
          <Button variant="link" onClick={onOpenSettings}>
            <Sliders size={16} />
          </Button>
        </StatsHeader>
        <StatsContent>
          <div className="text-center text-muted">
            <small>Processing batch inference...</small>
          </div>
        </StatsContent>
      </StatsContainer>
    )
  }

  if (isLoading) {
    return (
      <StatsContainer>
        <div className="inference-stats-container h-100">
          <div className="stats-wrapper">
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </StatsContainer>
    )
  }

  if (!predictionStats?.Body) {
    return (
      <StatsContainer>
        <div className="inference-stats-container h-100">
          <div className="stats-wrapper">
            <div className="text-center p-4">
              <p className="text-white">No statistics available</p>
            </div>
          </div>
        </div>
      </StatsContainer>
    )
  }

  return (
    <StatsContainer>
      <StatsHeader>
        <h5>Confidence Distribution</h5>
        <Button variant="link" onClick={onOpenSettings}>
          <Sliders size={16} />
        </Button>
      </StatsHeader>
      <StatsContent>
        <DistributionBar>
          <BarSegment
            $width={getPercentage(confidenceDistribution?.none || 0)}
            $color="#dc3545"
            onClick={() => onTabChange('none')}
            $isActive={activeTab === 'none'}
          />
          <BarSegment
            $width={getPercentage(confidenceDistribution?.low || 0)}
            $color="#fd7e14"
            onClick={() => onTabChange('low')}
            $isActive={activeTab === 'low'}
          />
          <BarSegment
            $width={getPercentage(confidenceDistribution?.medium || 0)}
            $color="#ffc107"
            onClick={() => onTabChange('medium')}
            $isActive={activeTab === 'medium'}
          />
          <BarSegment
            $width={getPercentage(confidenceDistribution?.high || 0)}
            $color="#198754"
            onClick={() => onTabChange('high')}
            $isActive={activeTab === 'high'}
          />
        </DistributionBar>
        <DistributionLegend>
          <LegendItem onClick={() => onTabChange('none')} $isActive={activeTab === 'none'}>
            <LegendColor $color="#dc3545" />
            <span>None ({confidenceDistribution?.none || 0})</span>
          </LegendItem>
          <LegendItem onClick={() => onTabChange('low')} $isActive={activeTab === 'low'}>
            <LegendColor $color="#fd7e14" />
            <span>Low ({confidenceDistribution?.low || 0})</span>
          </LegendItem>
          <LegendItem onClick={() => onTabChange('medium')} $isActive={activeTab === 'medium'}>
            <LegendColor $color="#ffc107" />
            <span>Medium ({confidenceDistribution?.medium || 0})</span>
          </LegendItem>
          <LegendItem onClick={() => onTabChange('high')} $isActive={activeTab === 'high'}>
            <LegendColor $color="#198754" />
            <span>High ({confidenceDistribution?.high || 0})</span>
          </LegendItem>
        </DistributionLegend>
      </StatsContent>
    </StatsContainer>
  )
}

export default InferenceStats
